/*
  BrickOvenBaker Flour Finder (sections/bob-flour-finder.liquid).
  One quiz for every bake: "What are you baking?", then 1–4 questions for that
  bake, then a best match and two alternatives.

  Data: sections/bob-ff-data.liquid, fetched per bake with the Section
  Rendering API (/collections/<bake collection>?section_id=bob-ff-data).
  Listings of the same flour in different bag sizes share a "Short name"
  (custom.short_name) and are scored as one flour; the bag size answer picks
  which listing to show.

  Scoring (higher is better):
  - Style: the flour's "Best for" (+60) or "Also good for" (+32) includes the
    chosen style; generic pizza/bread/pasta or all-purpose flours score less.
    Flours with no link to the chosen style are left out.
  - Oven (pizza): the flour's "Oven" field (custom.oven: Home oven / Pizza oven).
  - Dough time: strength (W, or the strength band) against the rise time;
    long-fermentation flours get a bonus for 48–72 h.
  - Grain (bread): flour type white / some whole grain / whole grain.
  - Bag size: a listing in the chosen size, if one is in stock (+12, or -20
    when the flour isn't available in that size; no change when the listing
    has no "Bag size" filled in).
  - Small tie-breaks: the collection's Top picks, "Push in collections",
    position in the bake collection, reviews.
  Only flours that can be bought are shown (request-only in Canada excluded).
*/
(function () {
  'use strict';

  var root = document.querySelector('[data-bob-ff]');
  if (!root) return;
  var stage = root.querySelector('[data-ff-stage]');
  var bar = root.querySelector('[data-ff-bar]');
  var ROOT = (root.getAttribute('data-root') || '/').replace(/\/?$/, '/');
  var CART_URL = root.getAttribute('data-cart-url') || '/cart';
  var CART_ADD = (root.getAttribute('data-cart-add') || '/cart/add') + '.js';
  var CUSTOMER = root.getAttribute('data-customer') || '';
  var SAVE_URL = root.getAttribute('data-save-url') || '';
  var COUNTRY = root.getAttribute('data-country') || '';
  var KEY = 'bobFlourFinder:v1';

  /* ---------- Questions ---------- */
  var PIZZA_STYLES = ['neapolitan-pizza', 'contemporary-pizza', 'new-york-pizza', 'chicago-pizza', 'roman-pizza-pinsa', 'detroit-sicilian-pizza'];

  var BAKES = {
    pizza: { l: 'Pizza', ic: 'cat_pizza', col: 'for-pizza', gen: ['pizza'], all: 'pizza flours', noun: 'pizza flour' },
    bread: { l: 'Bread & sourdough', ic: 'cat_bread', col: 'for-bread', gen: ['bread'], all: 'bread flours', noun: 'bread flour' },
    pastry: { l: 'Pastry & panettone', ic: 'cat_pastry', col: 'for-pastries', gen: ['pastries'], all: 'pastry flours', noun: 'pastry flour' },
    pasta: { l: 'Pasta', ic: 'cat_pasta', col: 'pasta', gen: ['pasta'], all: 'pasta flours', noun: 'pasta flour' },
    gf: { l: 'Gluten-free', ic: 'gf', col: 'gluten-free', gen: [], all: 'gluten-free flours', noun: 'gluten-free flour' }
  };

  var Q = {
    bake: {
      t: 'What are you baking?',
      o: [
        { v: 'pizza', l: 'Pizza', ic: 'cat_pizza' },
        { v: 'bread', l: 'Bread & sourdough', ic: 'cat_bread' },
        { v: 'pastry', l: 'Pastry & panettone', ic: 'cat_pastry' },
        { v: 'pasta', l: 'Pasta', ic: 'cat_pasta' },
        { v: 'gf', l: 'Gluten-free', ic: 'gf' }
      ]
    },
    pizzaStyle: {
      t: 'Which pizza?',
      o: [
        { v: 'neapolitan', l: 'Neapolitan', s: 'Soft, puffy, leopard-spotted crust', ic: 'neapolitan', m: ['neapolitan-pizza'], page: 'neapolitan-pizza-flour', all: 'Neapolitan pizza flours' },
        { v: 'contemporary', l: 'Contemporary & Canotto', s: 'Extra-airy, big puffy crust', ic: 'contemporary', m: ['contemporary-pizza'], page: 'contemporary-pizza-flour', all: 'contemporary pizza flours' },
        { v: 'newyork', l: 'New York', s: 'Foldable, crisp and chewy', ic: 'newyork', m: ['new-york-pizza'], page: 'ny-style-pizza-flour', all: 'New York-style pizza flours' },
        { v: 'pan', l: 'Pan, Roman & Detroit', s: 'Teglia, pinsa, Sicilian', ic: 'detroit', m: ['roman-pizza-pinsa', 'detroit-sicilian-pizza'], soft: ['focaccia'], page: 'pan-pizza-focaccia-flour', all: 'pan, Roman and Detroit pizza flours' },
        { v: 'any', l: 'A bit of everything', s: 'One flour for all kinds', ic: 'ap', m: ['pizza'], soft: PIZZA_STYLES, sw: 22, page: 'for-pizza', all: 'pizza flours' }
      ]
    },
    oven: {
      t: 'What will you bake it in?',
      o: [
        { v: 'home', l: 'Home oven', s: 'Up to about 550°F' },
        { v: 'steel', l: 'Home oven with a steel or stone', s: 'Hotter, crisper base' },
        { v: 'pizza', l: 'Pizza oven', s: '750°F and up (Ooni, wood-fired)' }
      ]
    },
    time: {
      t: 'How long does your dough rise?',
      o: [
        { v: 'short', l: 'Same day', s: 'Up to 8 hours' },
        { v: 'mid', l: 'Overnight', s: '12–24 hours' },
        { v: 'long', l: '48–72 hours', s: 'Cold rise, biga or poolish' }
      ]
    },
    breadType: {
      t: 'What kind of bread?',
      o: [
        { v: 'sourdough', l: 'Sourdough loaves', ic: 'loaf', m: ['sourdough'], page: 'sourdough-flour', all: 'sourdough flours' },
        { v: 'focaccia', l: 'Focaccia & pan breads', ic: 'focaccia', m: ['focaccia'], page: 'focaccia-flour', all: 'focaccia flours' },
        { v: 'baguette', l: 'Baguette & French bread', ic: 'baguette', m: ['baguette-french-breads'], page: 'baguette-french-bread-flour', all: 'baguette and French bread flours' },
        { v: 'everyday', l: 'Everyday loaves & rolls', ic: 'ap', m: ['bread'], soft: ['sourdough', 'focaccia'], sw: 12, page: 'for-bread', all: 'bread flours' }
      ]
    },
    grain: {
      t: 'White or whole grain?',
      o: [
        { v: 'white', l: 'White & light', s: 'Tipo 00 or 0, French T55–T65' },
        { v: 'some', l: 'Some whole grain', s: 'Tipo 1 or 2, stone-ground' },
        { v: 'whole', l: 'Whole grain', s: 'Integrale, whole wheat, rye' }
      ]
    },
    pastryType: {
      t: 'What are you making?',
      o: [
        { v: 'cakes', l: 'Cakes, cookies & tarts', ic: 'cake', m: ['cakes-cookies-tarts'], page: 'cake-flour', all: 'cake, cookie and tart flours' },
        { v: 'laminated', l: 'Croissant & laminated', ic: 'croissant', m: ['croissant-laminated'], page: 'croissant-flour', all: 'croissant flours' },
        { v: 'enriched', l: 'Panettone, brioche & enriched', ic: 'panettone', m: ['panettone-pandoro-colomba', 'brioche-enriched'], page: 'panettone-flour', all: 'panettone flours' },
        { v: 'crepes', l: 'Crêpes & pancakes', ic: 'crepe', m: ['crepes-waffles-pancakes'], page: 'crepe-flour', all: 'crêpe and pancake flours' }
      ]
    },
    pastaType: {
      t: 'What kind of pasta?',
      o: [
        { v: 'egg', l: 'Fresh egg pasta', s: 'Tagliatelle, ravioli, lasagne', ic: 'nest', m: ['fresh-egg-pasta'], page: 'fresh-pasta-flour', all: 'fresh pasta flours' },
        { v: 'gnocchi', l: 'Gnocchi', ic: 'gnocchi', m: ['gnocchi'], page: 'gnocchi-flour', all: 'gnocchi flours' },
        { v: 'durum', l: 'Durum semolina shapes', s: 'Orecchiette, cavatelli, extruded', ic: 'semola', m: ['pasta'], ft: ['Semolina/Durum', 'Semolina (durum)'], page: 'semolina-flour', all: 'semolina flours' }
      ]
    },
    gfUse: {
      t: 'What will you make?',
      o: [
        { v: 'Bread & Pizza', l: 'Bread & pizza', ic: 'cat_pizza', page: 'gluten-free', all: 'gluten-free flours' },
        { v: 'Pasta', l: 'Pasta', ic: 'cat_pasta', page: 'gluten-free', all: 'gluten-free flours' },
        { v: 'Pastry', l: 'Pastry & cookies', ic: 'cake', page: 'gluten-free', all: 'gluten-free flours' },
        { v: 'Enriched & Panettone', l: 'Panettone & enriched', ic: 'panettone', page: 'gluten-free', all: 'gluten-free flours' },
        { v: 'All-purpose', l: 'A bit of everything', ic: 'ap', page: 'gluten-free', all: 'gluten-free flours' }
      ]
    },
    wheat: {
      t: 'Do you need it wheat-free?',
      o: [
        { v: 'yes', l: 'Yes, wheat-free', s: 'No gluten-free wheat starch' },
        { v: 'no', l: 'No preference' }
      ]
    },
    size: {
      t: 'How much flour do you go through?',
      o: [
        { v: 'small', l: '1 kg bags', s: 'Bake now and then, or trying a flour' },
        { v: 'mid', l: '2–5 kg', s: 'Bake most weeks' },
        { v: 'bulk', l: '10–25 kg', s: 'Big batches or a pizzeria' }
      ]
    }
  };

  var PATHS = {
    pizza: ['pizzaStyle', 'oven', 'time', 'size'],
    bread: ['breadType', 'time', 'grain', 'size'],
    pastry: ['pastryType', 'size'],
    pasta: ['pastaType', 'size'],
    gf: ['gfUse', 'wheat']
  };
  var STYLE_Q = { pizza: 'pizzaStyle', bread: 'breadType', pastry: 'pastryType', pasta: 'pastaType', gf: 'gfUse' };

  var WHY_LABEL = {
    'neapolitan-pizza': 'Neapolitan pizza', 'contemporary-pizza': 'contemporary pizza', 'new-york-pizza': 'New York pizza', 'chicago-pizza': 'Chicago pizza',
    'roman-pizza-pinsa': 'Roman pizza and pinsa', 'detroit-sicilian-pizza': 'Detroit and Sicilian pizza', 'pizza': 'pizza', 'sourdough': 'sourdough',
    'focaccia': 'focaccia', 'baguette-french-breads': 'baguettes and French bread', 'bread': 'bread', 'cakes-cookies-tarts': 'cakes, cookies and tarts',
    'croissant-laminated': 'croissants and laminated dough', 'panettone-pandoro-colomba': 'panettone, pandoro and colomba',
    'brioche-enriched': 'brioche and enriched dough', 'crepes-waffles-pancakes': 'crêpes and pancakes', 'fresh-egg-pasta': 'fresh egg pasta',
    'gnocchi': 'gnocchi', 'pasta': 'pasta'
  };
  var BAND_W = { 'Low': 200, 'Medium': 270, 'Strong': 315, 'Very strong': 370 };
  var GRAIN = {
    white: ['Tipo 00', 'Tipo 0', 'French T45', 'French T55', 'French T65'],
    some: ['Tipo 1', 'Tipo 2', 'French T80', 'French T110', 'Ancient grains'],
    whole: ['Whole Wheat (Integrale)', 'French T130', 'French T150', 'Rye']
  };
  var SIZE_KG = { small: 1, mid: 4, bulk: 20 };

  /* ---------- State ---------- */
  var state = { bake: null, answers: {}, idx: 0, skipBake: false, started: false };

  function steps() { return state.bake ? ['bake'].concat(PATHS[state.bake]) : ['bake']; }
  function opt(qk, v) {
    var list = Q[qk].o;
    for (var i = 0; i < list.length; i++) if (list[i].v === v) return list[i];
    return null;
  }

  /* ---------- Helpers ---------- */
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function icon(k) {
    return k ? '<span class="bob-ff__ic"><svg class="bob-ic" aria-hidden="true" focusable="false"><use href="#bob-i-' + k + '"/></svg></span>' : '';
  }
  function has(arr, list) {
    if (!arr || !list) return false;
    for (var i = 0; i < list.length; i++) if (arr.indexOf(list[i]) > -1) return true;
    return false;
  }
  function firstMatch(arr, list) {
    if (!arr || !list) return null;
    for (var i = 0; i < arr.length; i++) if (list.indexOf(arr[i]) > -1) return arr[i];
    return null;
  }
  function bagKg(bag) {
    if (!bag) return null;
    var n = parseFloat(String(bag).replace(',', '.'));
    if (isNaN(n)) return null;
    if (/lb/i.test(bag)) n = n * 0.4536;
    else if (/\bg\b/i.test(bag) && !/kg/i.test(bag)) n = n / 1000;
    return n;
  }
  function sizeGroup(bag) {
    var n = bagKg(bag);
    if (n == null) return '';
    return n <= 1.5 ? 'small' : (n <= 6 ? 'mid' : 'bulk');
  }
  function wOf(p) {
    if (p.wa) return +p.wa;
    if (p.sb && BAND_W[p.sb]) return BAND_W[p.sb];
    return null;
  }
  function wText(p) {
    if (p.wr && p.wr.length) return 'W ' + String(p.wr[0]).replace('-', '–');
    if (p.wa) return 'W ' + p.wa;
    return '';
  }
  function isFlour(p) {
    return (p.bf && p.bf.length) || (p.ag && p.ag.length) || (p.ft && p.ft.length) || p.sb || p.wa || (p.gf && p.gf.length);
  }
  function isGF(p) {
    return (p.ft && p.ft.indexOf('Gluten-free') > -1) || (p.gf && p.gf.length > 0);
  }
  function displayName(p) {
    var t = p.sn || p.t;
    if (p.sn && p.bag) t += ' · ' + String(p.bag).replace(/\s+/g, '');
    return t;
  }
  function track(name, props) {
    props = props || {};
    try { if (window.amplitude && typeof window.amplitude.track === 'function') window.amplitude.track(name, props); } catch (e) {}
    try {
      if (typeof window.gtag === 'function') window.gtag('event', name, props);
      else if (window.dataLayer && window.dataLayer.push) window.dataLayer.push(Object.assign({ event: name }, props));
    } catch (e) {}
  }
  function progress(done, total) {
    if (bar) bar.style.width = Math.max(4, Math.min(100, Math.round(done / total * 100))) + '%';
  }

  /* ---------- Data ---------- */
  var cache = {};
  function load(bake) {
    if (cache[bake]) return cache[bake];
    var url = ROOT + 'collections/' + BAKES[bake].col + '?section_id=bob-ff-data';
    cache[bake] = fetch(url, { credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.text(); })
      .then(function (t) {
        var m = t.match(/<script type="application\/json" data-ff-data>([\s\S]*?)<\/script>/);
        if (!m) throw new Error('No flour data');
        return JSON.parse(m[1]);
      })
      .catch(function (e) { delete cache[bake]; throw e; });
    return cache[bake];
  }

  function group(data) {
    var map = {}, order = [];
    (data.p || []).forEach(function (p, i) {
      p.pos = i;
      var key = String(p.sn || p.t).toLowerCase();
      if (!map[key]) { map[key] = { key: key, items: [], minPos: i }; order.push(key); }
      map[key].items.push(p);
    });
    return order.map(function (k) {
      var line = map[k];
      line.base = line.items.filter(function (i) { return i.bf && i.bf.length; })[0] || line.items[0];
      return line;
    });
  }

  function topRank(data, line) {
    var tp = data.tp || '';
    var best = 0;
    var ids = tp.split('|').filter(Boolean);
    line.items.forEach(function (i) {
      var at = ids.indexOf(String(i.id));
      if (at > -1 && (best === 0 || at + 1 < best)) best = at + 1;
    });
    return best;
  }

  function pickListing(line, size) {
    var buy = line.items.filter(function (i) { return i.a && !i.rq; });
    if (!buy.length) return null;
    var target = SIZE_KG[size] || 1;
    buy.sort(function (a, b) {
      var ka = bagKg(a.bag), kb = bagKg(b.bag);
      var da = ka == null ? 99 : Math.abs(Math.log(ka / target));
      var db = kb == null ? 99 : Math.abs(Math.log(kb / target));
      return da - db;
    });
    var inSize = size ? buy.filter(function (i) { return sizeGroup(i.bag) === size; }) : [];
    // A listing with no bag size can't be ruled in or out: neutral (null), no size note
    var unknown = (size && !inSize.length) ? buy.filter(function (i) { return !sizeGroup(i.bag); }) : [];
    return { pick: inSize[0] || unknown[0] || buy[0], inSize: inSize.length ? true : (unknown.length ? null : false), others: buy };
  }

  /* ---------- Scoring ---------- */
  function score(line, A, data) {
    var p = line.base;
    var bake = state.bake;
    var bf = p.bf || [], ag = p.ag || [];
    var s = 0, why = [];
    if (!isFlour(p)) return null;

    if (bake === 'gf') {
      if (!isGF(p)) return null;
      var gfu = p.gf || [];
      if (gfu.indexOf(A.gfUse) > -1) { s += 60; }
      else if (gfu.indexOf('All-purpose') > -1) { s += 22; }
      else if (A.gfUse === 'All-purpose' && gfu.length) { s += 14; }
      else return null;
      if (A.wheat === 'yes') {
        if (p.wf !== true) return null;
        why.push('Wheat-free: no gluten-free wheat starch');
      }
      if (gfu.indexOf(A.gfUse) > -1 && A.gfUse !== 'All-purpose') why.unshift('Made for gluten-free ' + A.gfUse.toLowerCase().replace('&', 'and'));
      else why.unshift('All-purpose gluten-free blend');
    } else {
      if (isGF(p)) return null;
      if (bf.length === 1 && bf[0] === 'pizza-dusting') return null;
      var o = opt(STYLE_Q[bake], A[STYLE_Q[bake]]) || {};
      var strong = firstMatch(bf, o.m || []);
      var weak = !strong && firstMatch(ag, o.m || []);
      if (strong) { s += 60; why.push('Made for ' + (WHY_LABEL[strong] || String(o.l).toLowerCase())); }
      else if (weak) { s += 32; why.push('Also works well for ' + (WHY_LABEL[weak] || String(o.l).toLowerCase())); }
      if (o.soft && (has(bf, o.soft) || has(ag, o.soft))) s += (o.sw || 10);
      if (!strong && !weak && has(bf, BAKES[bake].gen)) { s += 14; why.push('A good all-round ' + BAKES[bake].noun); }
      if (has(bf, ['all-purpose'])) s += (o.v === 'any' || o.v === 'everyday') ? 18 : 6;
      if (o.ft && has(p.ft, o.ft)) { s += 40; why.push('Durum wheat semolina'); }
      if (s < 12) return null;

      if (bake === 'pizza' && A.oven) {
        var ov = p.ov || [];
        var home = ov.indexOf('Home oven') > -1, po = ov.indexOf('Pizza oven') > -1;
        if (ov.length) {
          if (A.oven === 'pizza') { if (po) { s += 18; why.push('Made for 750°F+ pizza ovens'); } else s -= 25; }
          else if (A.oven === 'home') { if (home) { s += 18; why.push('Bakes and browns well in a home oven'); } else s -= 25; }
          else if (home) { s += 14; why.push('Bakes and browns well in a home oven'); } else s -= 8;
        } else {
          var neaOnly = has(bf, ['neapolitan-pizza']) && !has(bf, ['new-york-pizza', 'roman-pizza-pinsa', 'detroit-sicilian-pizza', 'chicago-pizza', 'pizza', 'all-purpose', 'contemporary-pizza']);
          var homeOnly = has(bf, ['new-york-pizza', 'roman-pizza-pinsa', 'detroit-sicilian-pizza', 'chicago-pizza']) && !has(bf, ['neapolitan-pizza', 'contemporary-pizza']);
          if (A.oven === 'home' && neaOnly) s -= 12;
          if (A.oven === 'pizza' && homeOnly) s -= 12;
        }
      }

      if (A.time) {
        var w = wOf(p);
        if (w) {
          if (A.time === 'short') { if (w <= 290) { s += 15; why.push('Easy to work with for same-day dough'); } else if (w <= 320) s += 4; else s -= 12; }
          if (A.time === 'mid') { if (w >= 260 && w <= 340) { s += 15; why.push('Right strength for an overnight rise'); } else if (w >= 240 && w <= 370) s += 4; else s -= 8; }
          if (A.time === 'long') { if (w >= 320) { s += 15; why.push('Strong enough for 48–72 hour cold rises'); } else if (w >= 295) s += 4; else s -= 15; }
        }
        if (A.time === 'long' && has(bf.concat(ag), ['long-fermentation-biga'])) s += 10;
      }

      if (A.grain) {
        var ft = p.ft || [];
        if (ft.length) {
          if (has(ft, GRAIN[A.grain])) { s += 16; if (A.grain !== 'white') why.push(ft.join(', ') + (p.ml === 'Stone-ground' ? ', stone-ground' : '')); }
          else s -= 10;
        }
        if (A.grain !== 'white' && p.ml === 'Stone-ground') s += 4;
      }
    }

    var rank = topRank(data, line);
    if (rank) s += rank === 1 ? 10 : (rank === 2 ? 6 : 4);
    if (line.items.some(function (i) { return i.push; })) s += 5;
    s += 6 * (1 - line.minPos / Math.max(1, (data.p || []).length));
    if (p.rt >= 4.5 && p.rc >= 10) s += 3;

    var L = pickListing(line, A.size || (bake === 'gf' ? 'small' : ''));
    if (!L) return null;
    if (A.size && L.inSize !== null) s += L.inSize ? 12 : -20;

    return { line: line, score: s, why: why, pick: L.pick, others: L.others, rank: rank, inSize: L.inSize };
  }

  function rankAll(data, A) {
    var out = [];
    group(data).forEach(function (line) {
      var r = score(line, A, data);
      if (r) out.push(r);
    });
    out.sort(function (a, b) { return b.score - a.score; });
    return out;
  }

  /* ---------- Rendering ---------- */
  function renderQuestion() {
    var keys = steps();
    var k = keys[state.idx];
    var q = Q[k];
    var total = state.bake ? keys.length : 4;
    var shown = state.idx + (state.skipBake ? 0 : 1);
    var count = total - (state.skipBake ? 1 : 0);
    progress(state.idx, total + 1);

    var html = '<div class="bob-ff__card">';
    html += '<div class="bob-ff__top">';
    if (state.idx > 0) html += '<button type="button" class="bob-ff__back" data-ff-back>← Back</button>';
    html += '<span class="bob-ff__step">' + (state.bake && k !== 'bake' ? 'Question ' + shown + ' of ' + count : 'Question 1') + '</span></div>';
    html += '<h2 class="bob-ff__q" tabindex="-1">' + esc(q.t) + '</h2>';
    if (k === 'bake') html += resumeBanner();
    html += '<div class="bob-ff__opts' + (q.o.length === 3 ? ' --three' : '') + '">';
    q.o.forEach(function (o) {
      var on = state.answers[k] === o.v;
      html += '<button type="button" class="bob-ff__opt' + (on ? ' is-on' : '') + (o.ic ? '' : ' --plain') + '" data-ff-v="' + esc(o.v) + '" aria-pressed="' + on + '">' +
        icon(o.ic) + '<span class="bob-ff__opt-t"><span class="bob-ff__opt-l">' + esc(o.l) + '</span>' +
        (o.s ? '<span class="bob-ff__opt-s">' + esc(o.s) + '</span>' : '') + '</span></button>';
    });
    html += '</div></div>';
    stage.innerHTML = html;
    focusHeading();
  }

  function focusHeading() {
    var h = stage.querySelector('.bob-ff__q, .bob-ff__res-h');
    if (h && state.started) { try { h.focus({ preventScroll: true }); } catch (e) { h.focus(); } }
    var top = root.getBoundingClientRect().top;
    if (state.started && top < 0) root.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function loadingView() {
    progress(1, 1);
    stage.innerHTML = '<div class="bob-ff__card bob-ff__loading"><span class="bob-ff__spin" aria-hidden="true"></span><p>Finding your flour…</p></div>';
  }

  function card(r, i, A) {
    var p = r.pick;
    var spec = [];
    var sb = r.line.base.sb, wt = wText(r.line.base);
    if (sb || wt) spec.push([sb, wt].filter(Boolean).join(', '));
    if (r.line.base.pp) spec.push(String(Math.round(r.line.base.pp * 10) / 10).replace(/\.0$/, '') + '% protein');
    if (p.bag) spec.push(String(p.bag).replace(/\s+/g, ' '));
    var sizeNote = (A.size && r.inSize === false) ? 'Not available in ' + (opt('size', A.size) || {}).l + ' right now' : '';
    var sizes = r.others.filter(function (o) { return o.id !== p.id && o.bag; })
      .sort(function (a, b) { return (bagKg(a.bag) || 0) - (bagKg(b.bag) || 0); });
    var main = i === 0;
    var h = '<article class="bob-ff__res-card' + (main ? ' --main' : '') + '">';
    h += '<a class="bob-ff__res-media" href="' + esc(p.u) + '" data-ff-click="' + i + '" tabindex="-1" aria-hidden="true">' +
      (p.img ? '<img src="' + esc(p.img) + '" alt="" loading="lazy" width="480" height="480">' : '') + '</a>';
    h += '<div class="bob-ff__res-body">';
    if (main) h += '<span class="bob-ff__badge">Best match</span>';
    h += '<span class="bob-ff__res-vendor">' + esc(p.vn) + '</span>';
    h += '<h3 class="bob-ff__res-title"><a href="' + esc(p.u) + '" data-ff-click="' + i + '">' + esc(displayName(p)) + '</a></h3>';
    if (spec.length) h += '<p class="bob-ff__res-spec">' + esc(spec.join(' · ')) + '</p>';
    if (main && r.why.length) {
      h += '<ul class="bob-ff__why">';
      r.why.slice(0, 3).forEach(function (w) { h += '<li>' + esc(w) + '</li>'; });
      h += '</ul>';
    } else if (r.why.length) {
      h += '<p class="bob-ff__res-why">' + esc(r.why[0]) + '</p>';
    }
    if (sizeNote) h += '<p class="bob-ff__note">' + esc(sizeNote) + '</p>';
    h += '<div class="bob-ff__res-buy"><span class="bob-ff__price">' + esc(p.pr) + '</span>';
    if (p.rt && p.rc) h += '<span class="bob-ff__rating" aria-label="Rated ' + esc(p.rt) + ' out of 5 from ' + esc(p.rc) + ' reviews"><span aria-hidden="true">★</span> ' + esc((+p.rt).toFixed(1)) + ' <span class="bob-ff__rc">(' + esc(p.rc) + ')</span></span>';
    h += '</div>';
    h += '<div class="bob-ff__res-cta"><button type="button" class="bob-ff__add" data-ff-add="' + esc(p.vid) + '" data-ff-i="' + i + '">Add to cart</button>' +
      '<a class="bob-ff__view" href="' + esc(p.u) + '" data-ff-click="' + i + '">View flour</a></div>';
    if (sizes.length) {
      h += '<p class="bob-ff__sizes">Also in ' + sizes.slice(0, 4).map(function (o) {
        return '<a href="' + esc(o.u) + '">' + esc(String(o.bag).replace(/\s+/g, ' ')) + '</a>';
      }).join(' · ') + '</p>';
    }
    h += '</div></article>';
    return h;
  }

  var lastResults = [];
  function renderResults(res, A) {
    lastResults = res.slice(0, 3);
    progress(1, 1);
    var o = opt(STYLE_Q[state.bake], A[STYLE_Q[state.bake]]) || {};
    var seeAll = ROOT + 'collections/' + (o.page || BAKES[state.bake].col);
    var html = '<div class="bob-ff__res">';
    html += '<div class="bob-ff__top"><button type="button" class="bob-ff__back" data-ff-back>← Change answers</button></div>';
    if (!lastResults.length) {
      html += '<h2 class="bob-ff__res-h" tabindex="-1">No exact match in stock right now</h2>' +
        '<p class="bob-ff__intro">Browse the full range instead, or try a different answer.</p>' +
        '<div class="bob-ff__res-actions"><a class="bob-ff__all" href="' + esc(seeAll) + '">See all ' + esc(o.all || BAKES[state.bake].all) + ' →</a>' +
        '<button type="button" class="bob-ff__restart" data-ff-restart>Start over</button></div></div>';
      stage.innerHTML = html;
      focusHeading();
      return;
    }
    html += '<h2 class="bob-ff__res-h" tabindex="-1">Your flour match</h2>';
    html += '<p class="bob-ff__res-sum">' + esc(summary(A)) + '</p>';
    html += card(lastResults[0], 0, A);
    if (lastResults.length > 1) {
      html += '<h3 class="bob-ff__alt-h">Also a great fit</h3><div class="bob-ff__alts">';
      for (var i = 1; i < lastResults.length; i++) html += card(lastResults[i], i, A);
      html += '</div>';
    }
    html += '<div class="bob-ff__res-actions"><a class="bob-ff__all" href="' + esc(seeAll) + '">See all ' + esc(o.all || BAKES[state.bake].all) + ' →</a>' +
      '<button type="button" class="bob-ff__restart" data-ff-restart>Start over</button></div>';
    html += '</div>';
    stage.innerHTML = html;
    focusHeading();
  }

  function summary(A) {
    var parts = [BAKES[state.bake].l];
    PATHS[state.bake].forEach(function (k) {
      var o = opt(k, A[k]);
      if (o) parts.push(o.l);
    });
    return parts.join(' · ');
  }

  function resumeBanner() {
    var rec = readSaved();
    if (!rec || !rec.picks || !rec.picks.length || !BAKES[rec.bake]) return '';
    if (rec.c && rec.c !== COUNTRY) return ''; // a match saved in another country may not be sold here
    return '<div class="bob-ff__resume"><span>Your last match: <a href="' + esc(rec.picks[0].u) + '">' + esc(rec.picks[0].t) + '</a></span>' +
      '<button type="button" class="bob-ff__resume-btn" data-ff-resume>See all three again</button></div>';
  }

  /* ---------- Saving ---------- */
  function readSaved() {
    try {
      var rec = JSON.parse(localStorage.getItem(KEY) || 'null');
      if (rec && Date.now() - rec.at < 1000 * 60 * 60 * 24 * 180) return rec;
    } catch (e) {}
    return null;
  }
  function save(A) {
    var rec = {
      v: 1, at: Date.now(), c: COUNTRY, bake: state.bake, answers: A,
      picks: lastResults.map(function (r) { return { id: r.pick.id, u: r.pick.u, t: displayName(r.pick) }; })
    };
    try { localStorage.setItem(KEY, JSON.stringify(rec)); } catch (e) {}
    if (CUSTOMER && SAVE_URL) {
      try {
        fetch(SAVE_URL, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(rec) }).catch(function () {});
      } catch (e) {}
    }
  }

  /* ---------- Flow ---------- */
  function finish() {
    var A = state.answers, bake = state.bake;
    loadingView();
    load(bake).then(function (data) {
      var res = rankAll(data, A);
      renderResults(res, A);
      save(A);
      var props = { bake: bake, results: lastResults.map(function (r) { return r.pick.id; }).join(','), best: lastResults[0] ? displayName(lastResults[0].pick) : '' };
      PATHS[bake].forEach(function (k) { props[k] = A[k]; });
      track('flour_finder_complete', props);
    }).catch(function () {
      stage.innerHTML = '<div class="bob-ff__card"><h2 class="bob-ff__q" tabindex="-1">Something went wrong loading the flours.</h2>' +
        '<p class="bob-ff__intro">Please try again, or browse <a href="' + esc(ROOT + 'collections/' + BAKES[bake].col) + '">all ' + esc(BAKES[bake].all) + '</a>.</p>' +
        '<div class="bob-ff__res-actions"><button type="button" class="bob-ff__restart" data-ff-retry>Try again</button></div></div>';
      focusHeading();
    });
  }

  function answer(v) {
    var keys = steps();
    var k = keys[state.idx];
    if (!state.started) { state.started = true; track('flour_finder_start', { source: state.skipBake ? 'link' : 'page', bake: k === 'bake' ? v : state.bake }); }
    state.answers[k] = v;
    track('flour_finder_answer', { question: k, answer: v, bake: k === 'bake' ? v : state.bake });
    if (k === 'bake') {
      if (state.bake !== v) { state.answers = { bake: v }; }
      state.bake = v;
      load(v).catch(function () {});
    }
    keys = steps();
    if (state.idx + 1 < keys.length) { state.idx += 1; renderQuestion(); }
    else finish();
  }

  function back() {
    if (state.idx > 0) {
      var onResults = !!stage.querySelector('.bob-ff__res');
      if (!onResults) state.idx -= 1;
      renderQuestion();
    }
  }

  function restart() {
    state = { bake: null, answers: {}, idx: 0, skipBake: false, started: true };
    renderQuestion();
  }

  function addToCart(btn) {
    var vid = btn.getAttribute('data-ff-add');
    var i = +btn.getAttribute('data-ff-i');
    if (!vid) return;
    btn.disabled = true;
    btn.textContent = 'Adding…';
    fetch(CART_ADD, {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: +vid, quantity: 1 }] })
    }).then(function (r) {
      if (!r.ok) return r.json().then(function (j) { throw new Error(j && (j.description || j.message) || 'Could not add'); });
      return r.json();
    }).then(function () {
      btn.textContent = 'Added';
      btn.classList.add('is-added');
      var r = lastResults[i];
      track('flour_finder_add_to_cart', { bake: state.bake, rank: i + 1, product_id: r ? r.pick.id : '', product: r ? displayName(r.pick) : '' });
      openCart(btn);
    }).catch(function (e) {
      btn.disabled = false;
      btn.textContent = 'Add to cart';
      var msg = btn.parentNode.querySelector('.bob-ff__err') || document.createElement('p');
      msg.className = 'bob-ff__err';
      msg.textContent = e && e.message ? e.message : 'Could not add to cart. Please try again.';
      btn.parentNode.appendChild(msg);
    });
  }

  function openCart(btn) {
    var opened = false;
    try { if (typeof window.upcartRefreshCart === 'function') window.upcartRefreshCart(); } catch (e) {}
    try { if (typeof window.upcartOpenCart === 'function') { window.upcartOpenCart(); opened = true; } } catch (e) {}
    try { document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true })); } catch (e) {}
    if (!opened && btn && !btn.parentNode.querySelector('.bob-ff__cartlink')) {
      var a = document.createElement('a');
      a.className = 'bob-ff__cartlink';
      a.href = CART_URL;
      a.textContent = 'View cart →';
      btn.parentNode.appendChild(a);
    }
  }

  stage.addEventListener('click', function (e) {
    var t = e.target.closest('[data-ff-v],[data-ff-back],[data-ff-restart],[data-ff-retry],[data-ff-add],[data-ff-resume],[data-ff-click]');
    if (!t) return;
    if (t.hasAttribute('data-ff-v')) { answer(t.getAttribute('data-ff-v')); return; }
    if (t.hasAttribute('data-ff-back')) { back(); return; }
    if (t.hasAttribute('data-ff-restart')) { restart(); return; }
    if (t.hasAttribute('data-ff-retry')) { finish(); return; }
    if (t.hasAttribute('data-ff-add')) { addToCart(t); return; }
    if (t.hasAttribute('data-ff-resume')) {
      var rec = readSaved();
      if (rec && BAKES[rec.bake]) {
        state.started = true;
        state.bake = rec.bake;
        state.answers = rec.answers || { bake: rec.bake };
        state.idx = steps().length - 1;
        finish();
      }
      return;
    }
    if (t.hasAttribute('data-ff-click')) {
      var r = lastResults[+t.getAttribute('data-ff-click')];
      if (r) track('flour_finder_result_click', { bake: state.bake, rank: +t.getAttribute('data-ff-click') + 1, product_id: r.pick.id, product: displayName(r.pick) });
    }
  });

  /* ---------- Start (links can skip ahead: ?bake=pizza&style=neapolitan) ---------- */
  var params = new URLSearchParams(window.location.search);
  var qb = params.get('bake');
  if (qb === 'gluten-free') qb = 'gf';
  if (qb && BAKES[qb]) {
    state.bake = qb;
    state.answers.bake = qb;
    state.skipBake = true;
    state.idx = 1;
    load(qb).catch(function () {});
    var qs = params.get('style');
    var sk = STYLE_Q[qb];
    if (qs && opt(sk, qs)) {
      state.answers[sk] = qs;
      state.idx = 2;
    }
    if (state.idx >= steps().length) state.idx = steps().length - 1;
  }
  renderQuestion();
})();
